import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { generateSignatureHeaders } from './signature'

// 统一后端响应结构类型
type ApiResponse<T = unknown> = {
  code: number
  message?: string
  data: T
}

// 创建axios实例
const request: AxiosInstance = axios.create({
  // 仅使用环境变量配置，未配置则走相对路径
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10000, // 请求超时时间（10秒）
  headers: {
    'Content-Type': 'application/json',
  },
  // 强制使用浏览器 XHR 适配器，避免 fetch 适配器在某些环境下的 Request/Response 兼容问题
  adapter: 'xhr'
})

// 请求拦截器
request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 合并请求参数（URL参数 + Body参数）
    const params: Record<string, any> = {}
    
    // 添加 URL 查询参数
    if (config.params) {
      Object.assign(params, config.params)
    }
    
    // 添加 Body 参数（仅对 POST/PUT/PATCH 等请求）
    if (config.data && typeof config.data === 'object') {
      Object.assign(params, config.data)
    }
    
    // 生成签名头部
    const signatureHeaders = generateSignatureHeaders(params)
    
    // 在发送请求之前做些什么，比如添加token和签名
    const token = localStorage.getItem('token')
    config.headers = {
      ...(config.headers as any),
      ...signatureHeaders,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    } as any
    
    if (import.meta.env.DEV) {
      const method = (config.method || 'get').toUpperCase()
      const base = config.baseURL || ''
      const url = typeof config.url === 'string' ? config.url : ''
      // 打印最终请求路径（便于和代理终端日志核对）
      console.log(`[HTTP] ${method} ${base}${url}`)
      console.log('[HTTP] Signature Headers:', signatureHeaders)
    }
    return config
  },
  (error) => {
    // 对请求错误做些什么
    console.error('Request Error:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
request.interceptors.response.use(
  ((response: AxiosResponse<ApiResponse>) => {
    const payload = response.data as ApiResponse
    // 如果后端按 { code, message, data } 返回
    if (
      payload && typeof payload === 'object' && 'code' in payload && 'data' in payload
    ) {
      if (payload.code === 10000) {
        // 通过断言让拦截器可以直接返回 data
        return payload.data as any
      }
      const errMsg = payload.message || 'Request failed'
      if (import.meta.env.DEV) {
        console.error('[HTTP] API logical error:', payload)
      }
      return Promise.reject(new Error(errMsg))
    }
    // 兼容非标准响应，直接返回原始数据
    return (response.data as unknown) as any
  }) as any,
  (error) => {
    // 对响应错误做点什么
    console.error('Response Error:', error)
    if (import.meta.env.DEV) {
      const method = error?.config?.method?.toUpperCase?.() || 'GET'
      const base = error?.config?.baseURL || ''
      const url = error?.config?.url || ''
      const status = error?.response?.status
      console.error(`[HTTP] ${method} ${base}${url} -> ${status || 'ERR'}`)
    }
    
    if (error.response) {
      // 服务器返回了错误状态码
      switch (error.response.status) {
        case 401:
          // 未授权，跳转到登录页面
          console.error('Unauthorized, redirecting to login...')
          break
        case 403:
          // 禁止访问
          console.error('Forbidden')
          break
        case 404:
          // 请求不存在
          console.error('Not Found')
          break
        case 500:
          // 服务器错误
          console.error('Server Error')
          break
        default:
          console.error(`Request failed with status ${error.response.status}`)
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应
      console.error('No response received:', error.request)
    } else {
      // 其他错误
      console.error('Error:', error.message)
    }
    
    return Promise.reject(error)
  }
)

export default request